import React, { useState, useEffect } from 'react';
import Moment from 'moment-timezone';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, 
  Fade, FormControl, FormControlLabel, FormGroup, FormHelperText, IconButton, 
  Input, InputAdornment, InputLabel, Paper, Popper, 
  PopperPlacementType, Snackbar, SvgIcon, Switch, Tab, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography 
} from '@material-ui/core';
import { AddCircle as AddCircleIcon, Edit as EditIcon, 
  Delete as DeleteIcon, Close as CloseIcon, 
  WarningRounded as WarningIcon, CheckCircleRounded as CheckCircleIcon } from '@material-ui/icons';

import {useDispatch, useSelector} from "react-redux";
import {RootState} from "app/main/rootReducer";

import Alert from '@material-ui/lab/Alert';

import apiKeysService from '../../services/apiKeys.service';
import { IApiKeyList, ITabPanelProps, IUrlParameter } from './types';
import { getAll, add,remove, edit, handleModalToggle, resetSelectedApiKey, 
    updateSelectedApiKey, IApiKey, IApiKeyRequest, getPartners, addPartner, 
    IPartnerSite, setPartnerSites, IPartner } from 'app/reducers/apiKeySlice';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import appConstants from 'app/_helpers/app.constants';
import { useHistory, useParams } from 'react-router-dom';

const NS = 'ApiKeyList';

const useStyles = makeStyles((theme: Theme) => ({
  container:{
    display: 'flex',
    flexWrap: 'wrap',
  },
  table: {
    minWidth: 650,
  },
  addCircleIcon:{
    fontSize: 50,
  },
  addButton: {
    width: '100%',
    position: 'relative',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    display: 'flex',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '100%',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  dialogPaper: {
    minHeight: '80vh',
    minWidth: '100vh',
  },
  clipboardIcon: {
    marginLeft: theme.spacing(1),
  },
  typography:{
    padding: theme.spacing(2),
  },
  formControlLabel: {
    fontWeight: 600,
    '& .MuiTypography-root': {
      fontWeight: 700,
    },
  },
}));

const FormErrorKeys = { 
  posVendorNameErrorKey: 'POSVENDORNAMEERRORKEY', 
  partnerOAuthFlowErrorKey: 'PARTNEROAUTHFLOWERRORKEY',
  duplicatePosPartnerApiKey: 'DUPLICATEPOSPARTNERAPIKEY',
  posGiftCardErrorKey: 'POSGIFTCARDERRORKEY',
};
const filter = createFilterOptions<IPartner>();
const partnerSiteFilter = createFilterOptions<IPartnerSite>();

const ClipboardIcon = (props: any) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    props.handleClick(props.item as IApiKey, event)
  }

  return (
    <IconButton 
      onClick={handleClick}>
      <SvgIcon className={props.IconClassName}>
        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
      </SvgIcon>
    </IconButton>
  );
}

const CopiedPopper = (props: any) => {
  return (<Popper open={props.openClipboardCopiedPopper} {...props} transition disablePortal>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={1000}>
            <div>
              <Paper>
                <Typography className={props.typography}>copied!</Typography> 
              </Paper>
            </div>
          </Fade>
        )}
      </Popper>);
}

const DisplayApiKeyListTable = (props: any) => {
  return (<div>
    <CopiedPopper {...props} typography={props.classes.typography} />
    <TableContainer component={Paper}>
      <Table className={props.classes.table} aria-label="Api Key List">
        <TableHead>
          <TableRow>
            <TableCell>Integration Name</TableCell>
            <TableCell>Api Key</TableCell>
            <TableCell>Location Id</TableCell>
            <TableCell>Date Created</TableCell>
            <TableCell>Date Updated</TableCell>
            <TableCell>OAuth Flow Status</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.apiKeys.slice().sort((a: IApiKey, b: IApiKey) => a.id - b.id).map((row: IApiKey) => (
            <TableRow key={row.id}>
              <TableCell component="th" scope="row">{row.posVendorName}</TableCell>
              <TableCell>
                {row.apiKey.substring(0, appConstants.apiKeyDisplayLength)}...
                <ClipboardIcon 
                  IconClassName={props.classes.clipboardIcon} 
                  item={row} 
                  handleClick={props.handleClipboardClick}
                />
              </TableCell>
              <TableCell>{row.locationId}</TableCell>
              <TableCell>{Moment(row.dateCreated).format('YYYY-MM-DD')}</TableCell>
              <TableCell>{Moment(row.dateUpdated).format('YYYY-MM-DD')}</TableCell>
              <TableCell style={{textAlign: 'center'}}>
                {props.isPosEnabledForOAuthFlow(row) && <>
                  {(row.oAuthRefreshToken?.length <= 0 || row.locationId?.length <= 0) 
                    && <WarningIcon style={{fill: '#f0ad4e'}} />}
                  {(row.oAuthRefreshToken?.length > 0 && row.locationId?.length > 0) && <CheckCircleIcon style={{fill: '#c3e6cb'}} />}
                  </>
                }
                {!props.isPosEnabledForOAuthFlow(row) && <></>}
              </TableCell>
              <TableCell>
                <IconButton color="secondary" aria-label="Edit" component="span" onClick={() => props.handleOpenEditDialog(row)}>
                  <EditIcon color="action" fontSize="small" />
                </IconButton>
              </TableCell>
              <TableCell>
                <IconButton color="secondary" aria-label="Delete" component="span" onClick={() => props.handleDelete(row)}>
                  <DeleteIcon color="action" fontSize="small"/>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </div>)
}

const PartnerSitesForm = (props: any) => {
  return (<Dialog open={props.isModalOpen} classes={{ paper: props.classes.dialogPaper}} onClose={props.handleDialogClose} aria-labelledby="form-dialog-title">
    <DialogTitle id="form-dialog-title">Select Partner Site</DialogTitle>
          <DialogContent>
            <form id="myForm">
              <FormControl 
                className={props.classes.formControl} 
                // error={(getPosVendorNameErrorMessage()?.length ?? 0) > 0 }
              >
                <Autocomplete<IPartnerSite>
                  options={props.partnerSites}
                  value={props.selectedPartnerSite}
                  onChange={props.handlePartnerSiteSelectChange}
                  autoSelect
                  clearOnEscape
                  filterOptions={(options, params): IPartnerSite[] => {
                    return partnerSiteFilter(options, params);
                  }}
                  selectOnFocus
                  id="api-keys-select-partner-site"
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === 'string') {
                      return option;
                    }
                    
                    return option.name;
                  }}
                  renderOption={(option) => option.name}
                  style={{ display: 'contents' }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} label="Partner Site Name" variant="outlined" />
                  )}
                />
              </FormControl>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.handleDialogClose} id="btnCancel" color="primary">
              Cancel
            </Button>
            <Button onClick={props.handlePartnerSiteSelectSubmit} id="btnSave" color="secondary">
              Save
            </Button>
          </DialogActions>
  </Dialog>);
}

const TabPanel = (props: ITabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

const ApiKeyForm = (props: any) => {
  const getPosVendorNameErrorMessage = () => {
    return props.hasErrors?.find((f: any) => f.key === FormErrorKeys.posVendorNameErrorKey)?.message;
  };
  const getGiftCardTokenErrorMessage = () => {
    return props.hasServerValidationErrors?.find((f: any) => f.key === FormErrorKeys.posGiftCardErrorKey)?.message;
  };

  const a11yProps = (index: number) => {
    return {
      id: `api-key-form-tab-${index}`,
      'aria-controls': `api-key-form-tabpanel-${index}`
    };
  }

  return (<Dialog open={props.isModalOpen} classes={{ paper: props.classes.dialogPaper}} onClose={props.handleDialogClose} aria-labelledby="form-dialog-title">
          <DialogTitle classes={{ root: props.classes.formControlLabel}} id="form-dialog-title">Add New Api Key Integration</DialogTitle>
          <DialogContent>
            <form id="myForm">
              <Tabs
                value={props.selectedTab}
                onChange={props.handleTabsChange}
                indicatorColor='primary'
                textColor='primary'
                variant='fullWidth'
                aria-label='API Key Form Tabs'
              >
                {props.availableTabsLabels.map((availTabLabel: string, index: number) =>{
                  return <Tab key={index} label={availTabLabel} {...a11yProps(index)} />
                })}
              </Tabs>
              <TabPanel value={props.selectedTab} index={0}>
                <FormGroup>
                  <FormControl 
                    className={props.classes.formControl} 
                    error={(getPosVendorNameErrorMessage()?.length ?? 0) > 0 }
                  >
                    <Autocomplete<IPartner>
                      options={props.partners}
                      disabled = {!props.isNew}
                      value={props.selectedPosType}
                      onChange={props.handlePosVendorNameChange}
                      autoSelect
                      clearOnEscape
                      selectOnFocus
                      id="api-keys-select-or-add-pos-types"
                      getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                          return option;
                        }
                        
                        return option.name;
                      }}
                      renderOption={(option) => option.name}
                      style={{ display: 'contents' }}
                      freeSolo
                      renderInput={(params) => (
                        <TextField {...params} label="Pos Vendor Name" variant="outlined" />
                      )}
                    />
                    {(getPosVendorNameErrorMessage()?.length ?? 0) > 0 && 
                      <FormHelperText>{getPosVendorNameErrorMessage()}</FormHelperText>}
                  </FormControl>
                  <FormControl className={props.classes.formControl}>
                    <TextField id="location-id" 
                      label="Location Id" 
                      disabled={props.isPosEnabledForOAuthFlow(props.selectedApiKey)} 
                      value={props.selectedApiKey.locationId ?? ''} 
                      onChange={props.handleLocationIdChange} />
                  </FormControl>
                  {!props.isNew && <FormControl className={props.classes.formControl}>
                    <InputLabel htmlFor="api-key">Api Key</InputLabel>
                    <Input 
                      id="api-key" 
                      endAdornment={
                        <InputAdornment position="end">
                          <ClipboardIcon 
                            IconClassName={props.classes.clipboardIcon} 
                            item={props.selectedApiKey} 
                            handleClick={props.handleClipboardClick}
                          />
                          <CopiedPopper {...props} typography={props.classes.typography} />
                        </InputAdornment>
                      }
                      value={`${props.selectedApiKey.apiKey?.substring(0, appConstants.apiKeyDisplayLength)}...`} 
                      disabled
                    />
                  </FormControl>}
                </FormGroup>
              </TabPanel>
              <TabPanel value={props.selectedTab} index={1}>
                <FormGroup>
                <FormControl className={props.classes.formControl} error={!!props.bookingsIntegrationError}>
                  <FormControlLabel
                      label="Enable Bookings Integration"
                      classes={{ label: props.classes.formControlLabel}}
                      control={<Switch
                        checked={props.bookingsIntegrationChecked}
                        onChange={props.handleBookingsIntegrationCheckedChange}
                        name="checked"
                        color="primary"
                      />} />
                  {!!props.bookingsIntegrationError && <FormHelperText>{props.bookingsIntegrationError}</FormHelperText>}
                </FormControl>
                  { props.bookingsIntegrationChecked &&  <>
                    {
                      // Diplay the Lock Partner Bookings only if selectedPosTypeMarkLocationBookingsLocked (partners.MarkLocationBookingsLocked)
                      props.selectedPosTypeMarkLocationBookingsLocked &&
                      <FormControlLabel
                        label="Lock Partner Bookings"
                        classes={{ label: props.classes.formControlLabel}}
                        control={<Switch
                          // If isNew use the props.selectedPosTypeMarkLocationBookingsLocked, else if edit selectedApiKey.markLocationBookingsLocked (locations.MarkLocationBookingsLocked)
                          checked={props.isNew ? props.selectedPosTypeMarkLocationBookingsLocked : props.selectedApiKey.markLocationBookingsLocked}
                          onChange={props.handleVenueMarkBookingsLockedCheckedChange}
                          name="bookingLocked"
                          color="primary"
                        />}
                        // Lock Partner Bookings is Readonly. Venue needs to request support to amend / override the value.
                        disabled={true} />
                      }
                      <FormControlLabel
                        label="Enable Bookings Webhooks"
                        classes={{ label: props.classes.formControlLabel}}
                        control={<Switch 
                          checked={props.bookingsWebhookChecked}
                          onChange={props.handleBookingsWebhookCheckedChange}
                          name="checked"
                          color="primary"
                        />} 
                      />
                    {props.bookingsWebhookChecked && <FormControl className={props.classes.formControl}>
                      <TextField 
                        id="bookings-webhook-callbackurl" 
                        label="Bookings Webhook Url" 
                        value={props.bookingsCallbackUrl} 
                        onChange={props.handleBookingsCallbackUrlChange}
                      />
                      </FormControl>}
                  </>}
                </FormGroup>
              </TabPanel>
              <TabPanel value={props.selectedTab} index={2}>
                <FormGroup>
                <FormControl className={props.classes.formControl} error={!!props.bookingsIntegrationError}>
                  <FormControlLabel
                      label="Enable GiftCards Integration"
                      classes={{ label: props.classes.formControlLabel}}
                      control={<Switch
                        checked={props.giftCardsIntegrationChecked}
                        onChange={props.handleGiftCardsIntegrationCheckedChange}
                        name="checked"
                        color="primary"
                      />} />
                  {!!props.bookingsIntegrationError && <FormHelperText>{props.bookingsIntegrationError}</FormHelperText>}
                </FormControl>
                  { props.giftCardsIntegrationChecked && <FormControlLabel
                    label="Enable Giftcard Webhook"
                    classes={{ label: props.classes.formControlLabel}}
                    control={<Switch
                      checked={props.giftcardCallbackChecked}
                      onChange={props.handleGiftcardCallbackCheckedChange}
                      name="checked"
                      color="primary"
                    />} /> }
                  { props.giftCardsIntegrationChecked && props.giftcardCallbackChecked && 
                    <FormControl className={props.classes.formControl}>
                      <TextField
                        id="giftcard-webhook-callbackurl"
                        label="Giftcard Webhook Url"
                        value={props.giftcardCallbackUrl}
                        onChange={props.handleGiftcardCallbackUrlChange}
                      />
                    </FormControl>
                  }
                  {props.giftCardsIntegrationChecked && props.giftcardTokenVisible && props.giftcardCallbackChecked && 
                    <FormControl className={props.classes.formControl}
                      error={(getGiftCardTokenErrorMessage()?.length ?? 0) > 0}
                    >
                      <TextField
                        id="giftcard-webhook-callbackurl-securityToken"
                        label="Giftcard Webhook Url Security Token"
                        value={props.giftcardCallbackUrlSecurityToken}
                        onChange={props.handleGiftcardCallbackSecurityTokenChange}
                      />
                      <FormHelperText>{getGiftCardTokenErrorMessage()}</FormHelperText>
                    </FormControl>
                  } 
                </FormGroup>
              </TabPanel>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.handleDialogClose} id="btnCancel" color="primary">
              Cancel
            </Button>
            <Button onClick={props.handleSaveClick} id="btnSave" color={props.saveHasError ? 'secondary' : 'primary'}>
              Save
            </Button>
            {!props.isNew && props.isPosEnabledForOAuthFlow(props.selectedApiKey) &&  
                  <Button onClick={() => props.handleOAuthFlowReconnect(true, props.selectedApiKey)} 
                    id="btnPartnerOAuthConnect" color="primary">Connect</Button>
            }
          </DialogActions>
        </Dialog>);
}

export default function ApiKeyList({ handleSomeClick }: IApiKeyList) {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { locationId } = useParams<IUrlParameter>();
  const { isModalOpen, partners, apiKeys, selectedApiKey, partnerSites, selectedPartnerSite } = useSelector((state: RootState) => state.apiKeysReducer);
  const { selectedVenue } = useSelector((state: RootState) => state.venueReducer);
  const [hasErrors, setHasErrors] = useState<{ key: string, message: string }[]>();
  const [hasServerValidationErrors, setHasServerValidationErrors] = useState<{ key: string, message: string }[]>();
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isPartnerSitesView, setIsPartnerSitesView] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openClipboardCopiedPopper, setOpenClipboardCopiedPopper] = useState(false);
  const [error, setError] = useState({message: ''});
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const openClipboardCopiedPopperRef: React.MutableRefObject<boolean> = React.useRef(openClipboardCopiedPopper);
  openClipboardCopiedPopperRef.current = openClipboardCopiedPopper;
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [selectedPosType, setSelectedPosType] = useState<IPartner | null>(null);
  const [isLoadingApiKeyList, setIsLoadingApiKeyList] = useState<boolean>(true);
  const [bookingsEventCallbackUrl, setBookingsEventCallbackUrl] = useState<string | null>(null);
  const [bookingsWebhookChecked, setBookingsWebhookChecked] = useState<boolean>(false);
  const [onEditPopUpOpenSelectedApiKey, setOnEditPopUpOpenSelectedApiKey] = useState<IApiKey>(null)
  const [giftcardTokenVisible, setGiftcardTokenVisible] = useState<boolean>(false);
  const [giftcardCallbackUrl, setGiftcardEventCallbackUrl] = useState<string | null>(null);
  const [giftcardCallbackUrlSecurityToken, setGiftcardCallbackUrlSecurityToken] = useState<string | null>(null);
  const [giftcardCallbackChecked, setGiftcardCallbackChecked] = useState<boolean>(false);
  const [selectedPosTypeMarkLocationBookingsLocked, setSelectedPosTypeMarkLocationBookingsLocked] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [bookingsIntegrationChecked, setBookingsIntegrationChecked] = useState<boolean>(false);
  const [giftCardsIntegrationChecked, setGiftCardsIntegrationChecked] = useState<boolean>(false);
  const [bookingsIntegrationError, setBookingsIntegrationError] = useState<string | null>(null);

  useEffect(() => {
    setHasErrors([]);
    setHasServerValidationErrors([]);
    setIsLoadingApiKeyList(true);
    if (selectedVenue && selectedVenue.id) {
      apiKeysService.getAvailablePartners()
        .then((data) => {
          dispatch(getPartners(data));
        });
      apiKeysService.getAll(selectedVenue.id)
        .then((venuesApikeys) => {
          if (venuesApikeys) {
            dispatch(getAll(venuesApikeys));
          } else {
            setError({message: 'Failed to retrieve Api Key List.'});
            setOpenErrorSnackbar(true);
          }
          setIsLoadingApiKeyList(false);
        });
    }
  }, [dispatch, selectedVenue]);

  useEffect(() => {
    if (!isLoadingApiKeyList && locationId && partnerSites.length > 0) {
      const locationIdAsInt = parseInt(locationId);
      const editApiKeyItem = apiKeys.find(f => f.id == locationIdAsInt);
      setIsPartnerSitesView(true);
      handleOpenEditDialog(editApiKeyItem);
    }
  }, [locationId, isLoadingApiKeyList]);

  useEffect(() => {
    if (selectedPosType) {
      const updatedApiKeyItem = { ...selectedApiKey, posTypeId: selectedPosType.id, posVendorName: selectedPosType.name } as IApiKey;
      setSelectedPosTypeMarkLocationBookingsLocked(selectedPosType.markBookingsLocked);
      dispatch(updateSelectedApiKey(updatedApiKeyItem));
    }
  }, [dispatch, selectedPosType]);

  const validatePosVendorNameInput = (posVendorName: string) => {
    const posType = partners.find(f => posVendorName && f.name.toLowerCase() === posVendorName.toLowerCase());
    const posVendorNameErrorKeyIndex = hasErrors.findIndex(f => f.key === FormErrorKeys.posVendorNameErrorKey);
    if (!posType) { 
      if (posVendorNameErrorKeyIndex < 0) {
        setHasErrors([
          ...hasErrors, 
          { key: FormErrorKeys.posVendorNameErrorKey, message: 'The Pos Type is required.' }
        ]);
      }
      return false;
    } else {
      if (posVendorNameErrorKeyIndex >= 0) {
        setHasErrors([
          ...hasErrors.slice(0, posVendorNameErrorKeyIndex), 
          ...hasErrors.slice(posVendorNameErrorKeyIndex + 1)
        ]);
      }
      return true;
    }
  }

  const handlePosVendorNameChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    let thePosVendor: IPartner;
    if (typeof(newValue) === "string") {
      thePosVendor = partners.find(f => f.name.toLowerCase() === newValue?.toLowerCase());
    } else {
      thePosVendor = newValue;
    }
    const isValid = validatePosVendorNameInput(thePosVendor?.name);
    if (isValid) {
      dispatch(updateSelectedApiKey({...selectedApiKey, markLocationBookingsLocked: thePosVendor.markBookingsLocked }));
      setSelectedPosType(thePosVendor);
    } else {
      dispatch(updateSelectedApiKey({...selectedApiKey, markLocationBookingsLocked: false}));
      setSelectedPosType(null);
    }
  };

  const handleLocationIdChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const val = event.target.value as string;
    dispatch(updateSelectedApiKey({...selectedApiKey, locationId: val }));
  }

  const handleAddOrEdit = async () => {
    // Validate Pos Vendor Name
    if (!validatePosVendorNameInput(selectedPosType?.name))
      return;

    // Add When there are no Errors
    if (selectedApiKey && ((hasErrors?.length ?? 0) === 0)) {
      // The selected item is new and does not have id yet.

        var apiKeyRequest: IApiKeyRequest = { ...selectedApiKey };

        if (selectedApiKey.giftcardCallbackUrl && selectedApiKey.giftcardCallbackUrl.trim()) {
            apiKeyRequest.giftcards = { webhookUrl: selectedApiKey.giftcardCallbackUrl, securityToken: selectedApiKey.giftcardCallbackUrlSecurityToken }
        }

      var saveSucceeded: boolean = false;
      if (isNew && !selectedApiKey.id) {
        // An Active Api Key Item with same PosType already exists.
        if (apiKeys.find(f => f.posTypeId === selectedApiKey.posTypeId && f.isActive)) {
          setHasErrors([
            ...hasErrors,
            { key:FormErrorKeys.duplicatePosPartnerApiKey, message: `Api Key for '${selectedApiKey.posVendorName}' already exists`}
          ]);
          return;
        }

        const { newApiKey, errorMessage } = await apiKeysService.postNewApiKey(selectedVenue.id, apiKeyRequest);
        if (newApiKey) {
          dispatch(add(newApiKey));
          if (selectedPosType.enablePartnerOAuthFlow) {
            await handleOAuthFlowReconnect(false, newApiKey); 
          }
          saveSucceeded = true;
        } else {
          handleServerResponseError(apiKeyRequest, "new", errorMessage);
        }        
      } else {
        const { editedApiKey, errorMessage } = await apiKeysService.editApiKey(selectedVenue.id, apiKeyRequest);
        if (editedApiKey) {
          dispatch(edit(editedApiKey));
          saveSucceeded = true;
        } else {
          handleServerResponseError(apiKeyRequest, "edit", errorMessage);
        }
      }

      if (saveSucceeded) {
        setSelectedPosType(null);
        setBookingsEventCallbackUrl(null);
        setBookingsWebhookChecked(false);
        setGiftcardEventCallbackUrl(null);
        setGiftcardCallbackChecked(false);
        setGiftcardCallbackUrlSecurityToken(null);
        setOnEditPopUpOpenSelectedApiKey(null);
        setHasServerValidationErrors([]);
        handleDialogClose();
        setBookingsIntegrationChecked(false);
        setGiftCardsIntegrationChecked(false);
      }
    }
  }

  const handleServerResponseError = (apiKeyRequest: IApiKeyRequest, operation: string, errorMessage: string) => {
    setError({ message: `Failed to edit Api key for ${apiKeyRequest.posVendorName}` });
    if (errorMessage.includes('Giftcard Webhook')) {
      setHasServerValidationErrors([
        ...hasServerValidationErrors,
        { key: FormErrorKeys.posGiftCardErrorKey, message: "Invalid Webhook Callback Url / Token" }
      ]);
    }
  }


  const handleDialogClose = () => {
    setIsNew(false);
    setBookingsEventCallbackUrl(null);
    setBookingsWebhookChecked(false);
    setBookingsIntegrationError(null);
    setGiftcardTokenVisible(false);
    setGiftcardEventCallbackUrl(null);
    setGiftcardCallbackChecked(false);
    setGiftcardCallbackUrlSecurityToken(null);
    setOnEditPopUpOpenSelectedApiKey(null);
    setIsPartnerSitesView(false);
    dispatch(setPartnerSites([]));
    dispatch(handleModalToggle(false));
    setSelectedPosTypeMarkLocationBookingsLocked(false);
    setSelectedTab(0);
    setBookingsIntegrationChecked(false);
    setGiftCardsIntegrationChecked(false);
  }

  const handleOpenNewDialog = () => {
    setIsNew(true);
    setGiftcardTokenVisible(true);
    dispatch(resetSelectedApiKey());
    dispatch(handleModalToggle(true));
    setSelectedPosTypeMarkLocationBookingsLocked(false);
  }

  const handleOpenEditDialog = (editApiKeyItem: IApiKey) => {
    setIsNew(false);
    setOnEditPopUpOpenSelectedApiKey(editApiKeyItem);
    setSelectedPosTypeMarkLocationBookingsLocked(false);
    const posType = partners.find(f => f.id === editApiKeyItem.posTypeId);
      setSelectedPosType(posType);

    // reset
    setBookingsWebhookChecked(false);
    setBookingsEventCallbackUrl(null);
    setGiftcardCallbackChecked(false);
    setGiftcardEventCallbackUrl(null);

    //evaluate
    if ((editApiKeyItem?.callbackUrl?.length ?? 0) > 0) {
      setBookingsWebhookChecked(true);
      setBookingsEventCallbackUrl(editApiKeyItem.callbackUrl);
    }
    if ((editApiKeyItem?.giftcardCallbackUrl?.length ?? 0) > 0) {
      setGiftcardCallbackChecked(true);
      setGiftcardEventCallbackUrl(editApiKeyItem.giftcardCallbackUrl);
    }

    setBookingsIntegrationChecked(editApiKeyItem?.isBookingsIntegrationEnabled ?? false);
    setGiftCardsIntegrationChecked(editApiKeyItem?.isGiftCardsIntegrationEnabled ?? false);
    dispatch(updateSelectedApiKey(editApiKeyItem));
    dispatch(handleModalToggle(true));
  } 

  const handleDelete = async (deleteApiKeyItem: IApiKey) => {
    const result = await apiKeysService.deleteApiKey(selectedVenue.id, deleteApiKeyItem.id);
    if (result === true) {
      dispatch(remove(deleteApiKeyItem));
    } else {
      setError({message: `Failed to delete Api key for ${deleteApiKeyItem.posVendorName}`});
    }
  }

  const handleClipboardClick = (apiKeyItemToCopy: IApiKey, event: React.MouseEvent<HTMLButtonElement>) => {
    navigator.permissions.query({name: ("clipboard-write" as PermissionName)}).then(result => {
      if (result.state === "granted" || result.state === "prompt") {
        /* write to clipboard now */
        navigator.clipboard.writeText(apiKeyItemToCopy.apiKey).then(() => {
          setAnchorEl(event.target as HTMLButtonElement);
          setOpenClipboardCopiedPopper(!openClipboardCopiedPopper);
          setPlacement("bottom-end");
          setTimeout(() => 
            setOpenClipboardCopiedPopper(!openClipboardCopiedPopperRef.current)
          , 1500);
        });
      }
    });
  }

  const handleSnackbarClose = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({message: ''});
    setOpenErrorSnackbar(false);
  }

  const handleFormClose = () => {
    setHasErrors([]);
    setHasServerValidationErrors([]);
    setSelectedPosType(null);
    handleDialogClose(); 
  }

  const handlePartnerSiteSelectChange = async (event: React.ChangeEvent<{}>, newValue: IPartnerSite) => {
    if (newValue && newValue.id && newValue.id.length > 0 && selectedApiKey) {
      const updatedApiKeyItem = {...selectedApiKey, locationId: newValue.id, companyId: `${newValue.company_id}` } as IApiKey;
      dispatch(updateSelectedApiKey(updatedApiKeyItem));
    }
  }

  const handlePartnerSiteSelectSubmit = async () => {
    console.log('submitting partner site selection');
    const { editedApiKey, errorMessage } = await apiKeysService.updateApiKeyLocationId(selectedVenue.id,
      selectedApiKey);
    if (editedApiKey) {
      dispatch(edit(editedApiKey));
    } else {
      setError({message: `Failed to edit Api key for ${selectedApiKey.posVendorName}. ${errorMessage}`});
      setOpenErrorSnackbar(true);
      dispatch(resetSelectedApiKey());
    }
    dispatch(setPartnerSites([]));
    setSelectedPosType(null);
    setBookingsEventCallbackUrl(null);
    setBookingsWebhookChecked(false);
    setGiftcardEventCallbackUrl(null);
    setGiftcardCallbackChecked(false);
    handleDialogClose();
    setIsPartnerSitesView(false);
    history.push('/apikeys');
  }

  const handleBookingsCallbackUrlChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const val = event.target.value as string;
    dispatch(updateSelectedApiKey({...selectedApiKey, callbackUrl: val }));
    setBookingsEventCallbackUrl(val);
  }

  const handleBookingsWebhookCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
    setBookingsWebhookChecked(val);
    if (val === false) {
      dispatch(updateSelectedApiKey({...selectedApiKey, callbackUrl: null}));
      setBookingsEventCallbackUrl(null);
    }
  }

  const handleGiftCardsIntegrationCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
    setGiftCardsIntegrationChecked(val);
    if (val) {
      setBookingsIntegrationError(null);
    }
    if (selectedApiKey.isGiftCardsIntegrationEnabled !== val) {
      const giftCardsCallbackUrlValue = (val === false) ? null : selectedApiKey.callbackUrl;
      const giftCardsCallbackUrlSecurityTokenValue = (val === false) ? null : selectedApiKey.giftcardCallbackUrlSecurityToken;
      setGiftcardCallbackChecked(val === false ? false : giftcardCallbackChecked);
      dispatch(updateSelectedApiKey({
        ...selectedApiKey,
        isGiftCardsIntegrationEnabled: val,
        giftcardCallbackUrl: giftCardsCallbackUrlValue,
        giftcardCallbackUrlSecurityToken: giftCardsCallbackUrlSecurityTokenValue
      }));
      setGiftcardEventCallbackUrl(giftCardsCallbackUrlValue);
      setGiftcardCallbackUrlSecurityToken(giftCardsCallbackUrlSecurityTokenValue);
    }
  }

  const handleBookingsIntegrationCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
    setBookingsIntegrationChecked(val);
    if (val) {
      setBookingsIntegrationError(null);
    }
    if (selectedApiKey.isBookingsIntegrationEnabled !== val) {
      const bookingsCallbackUrlValue = (val === false) ? null : selectedApiKey.callbackUrl;
      setBookingsWebhookChecked(val === false ? false : bookingsWebhookChecked);
      dispatch(updateSelectedApiKey({
        ...selectedApiKey, 
        isBookingsIntegrationEnabled: val, 
        callbackUrl: bookingsCallbackUrlValue
      }));
      setBookingsEventCallbackUrl(bookingsCallbackUrlValue);
    }
  }

  const handleGiftcardCallbackUrlChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const val = event.target.value as string;
    dispatch(updateSelectedApiKey({ ...selectedApiKey, giftcardCallbackUrl: val }));
    setGiftcardEventCallbackUrl(val);

    const visible = val && val.trim() && val?.toUpperCase() !== onEditPopUpOpenSelectedApiKey?.giftcardCallbackUrl?.toUpperCase();
      setGiftcardTokenVisible(visible);

      if (!visible) {
        setGiftcardCallbackUrlSecurityToken(null);
      }
  }

  const handleGiftcardCallbackSecurityTokenChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const val = event.target.value as string;
      dispatch(updateSelectedApiKey({ ...selectedApiKey, giftcardCallbackUrlSecurityToken: val }));
      setGiftcardCallbackUrlSecurityToken(val);
  }

  const handleGiftcardCallbackCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
      setGiftcardCallbackChecked(val);
      if (val === false) {
      dispatch(updateSelectedApiKey({ ...selectedApiKey, giftcardCallbackUrl: null }));
      setGiftcardEventCallbackUrl(null);
        setGiftcardCallbackUrlSecurityToken(null);
      }
      else {
        setGiftcardTokenVisible(true);
      }
  }

  const handleVenueMarkBookingsLockedCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
    dispatch(updateSelectedApiKey({...selectedApiKey, markLocationBookingsLocked: val}));
  }

  const handleOAuthFlowReconnect = async (isDirty: boolean, theApiKey: IApiKey) => {
    let theUpdatedApiKeyItem = theApiKey;
    if (isDirty) {
      console.log('save the api key before redirecting to oauth flow');
      const { editedApiKey, errorMessage } = await apiKeysService.editApiKey(selectedVenue.id, theApiKey);
      if (editedApiKey) {
        dispatch(edit(editedApiKey));
        theUpdatedApiKeyItem = editedApiKey;
      } else {
        setError({message: `Failed to edit Api key for ${theUpdatedApiKeyItem.posVendorName}`});
        dispatch(resetSelectedApiKey());
      }
    }

    const thePartner = partners.find(f => f.id == selectedPosType.id);
    const uri = apiKeysService.buildOAuthFlowUri(selectedVenue.id, thePartner.clientId, theUpdatedApiKeyItem);
    window.open(uri, '_self');
  }

  const isPosEnabledForOAuthFlow = (rowItem: IApiKey) => {
    if (!partners || !rowItem) return false;
    return partners.findIndex(f => f.id == rowItem.posTypeId && f.enablePartnerOAuthFlow) >= 0;
  }

  const handleTabsChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  }

  const availableTabsLabels = ['Partner Details', 'Bookings', 'Gift Cards']

  const handleSaveClick = () => {
    const isPosVendorValid = validatePosVendorNameInput(selectedPosType?.name);
    if (!isPosVendorValid) {
      setSelectedTab(0);
      return;
    }
    if (!bookingsIntegrationChecked && !giftCardsIntegrationChecked) {
      setSelectedTab(1);
      setBookingsIntegrationError('Please enable at least one integration (Bookings or Gift Cards) before saving.');
      return;
    }
    setBookingsIntegrationError(null);
    handleAddOrEdit();
  }

  const saveHasError = !!bookingsIntegrationError || (hasErrors?.length ?? 0) > 0 || (hasServerValidationErrors?.length ?? 0) > 0;

  return (
    <div>
        <Snackbar 
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={openErrorSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          action={
            <>
              <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackbarClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
          >
            <Alert onClose={handleSnackbarClose} severity="error">
              {error.message}
            </Alert>
          </Snackbar>
      <div className={classes.addButton}>
        <IconButton color="primary" aria-label="Add New" disabled={isLoadingApiKeyList} component="span" onClick={handleOpenNewDialog} >
          <AddCircleIcon className={classes.addCircleIcon}/>
        </IconButton>
      </div>
      {
        isPartnerSitesView &&
        <PartnerSitesForm 
          isModalOpen={isModalOpen}
          classes={classes}
          handleDialogClose={handleFormClose}
          partnerSites={partnerSites}
          selectedPartnerSite={selectedPartnerSite}
          handlePartnerSiteSelectChange={handlePartnerSiteSelectChange}
          handlePartnerSiteSelectSubmit={handlePartnerSiteSelectSubmit}
        />
      }
      { !isPartnerSitesView &&
      <ApiKeyForm 
        isModalOpen={isModalOpen}
        classes={classes}
        handleDialogClose={handleFormClose}
        hasErrors={hasErrors}
        hasServerValidationErrors={hasServerValidationErrors}
        isNew={isNew} 
        giftcardTokenVisible={giftcardTokenVisible}
        partners={partners}
        selectedPosType={selectedPosType}
        handlePosVendorNameChange={handlePosVendorNameChange}
        selectedApiKey={selectedApiKey}
        handleLocationIdChange={handleLocationIdChange}
        handleClipboardClick={handleClipboardClick}
        openClipboardCopiedPopper={openClipboardCopiedPopper}
        anchorEl={anchorEl}
        placement={placement}
        handleAddOrEdit={handleAddOrEdit}
        bookingsWebhookChecked = {bookingsWebhookChecked}
        handleBookingsWebhookCheckedChange={handleBookingsWebhookCheckedChange}
        bookingsCallbackUrl={bookingsEventCallbackUrl}
        handleBookingsCallbackUrlChange={handleBookingsCallbackUrlChange}
        saveHasError={saveHasError}
        giftcardCallbackChecked={giftcardCallbackChecked}
        handleGiftcardCallbackCheckedChange={handleGiftcardCallbackCheckedChange}
        giftcardCallbackUrl={giftcardCallbackUrl}
        handleGiftcardCallbackUrlChange={handleGiftcardCallbackUrlChange}
        handleSaveClick={handleSaveClick}
        giftcardCallbackUrlSecurityToken={giftcardCallbackUrlSecurityToken}
        handleGiftcardCallbackSecurityTokenChange={handleGiftcardCallbackSecurityTokenChange}
        handleVenueMarkBookingsLockedCheckedChange={handleVenueMarkBookingsLockedCheckedChange}
        handleOAuthFlowReconnect={handleOAuthFlowReconnect}
        isPosEnabledForOAuthFlow={isPosEnabledForOAuthFlow} 
        selectedPosTypeMarkLocationBookingsLocked={selectedPosTypeMarkLocationBookingsLocked}
        selectedTab={selectedTab}
        handleTabsChange={handleTabsChange}
        availableTabsLabels={availableTabsLabels}
        bookingsIntegrationChecked={bookingsIntegrationChecked}
        bookingsIntegrationError={bookingsIntegrationError}
        handleBookingsIntegrationCheckedChange={handleBookingsIntegrationCheckedChange}
        giftCardsIntegrationChecked={giftCardsIntegrationChecked}
        handleGiftCardsIntegrationCheckedChange={handleGiftCardsIntegrationCheckedChange}
      /> }
      <DisplayApiKeyListTable 
        openClipboardCopiedPopper={openClipboardCopiedPopper}
        anchorEl={anchorEl}
        placement={placement}
        classes={classes}
        apiKeys={apiKeys}
        handleClipboardClick={handleClipboardClick}
        handleOpenEditDialog={handleOpenEditDialog}
        handleDelete={handleDelete}
        isPosEnabledForOAuthFlow={isPosEnabledForOAuthFlow}
      />
    </div>
  );
}